import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import CategoryIcon from '@mui/icons-material/Category';
import EditIcon from '@mui/icons-material/Edit';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import KitchenIcon from '@mui/icons-material/Kitchen';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import HomeRepairServiceIcon from '@mui/icons-material/HomeRepairService';
import OpacityIcon from '@mui/icons-material/Opacity';
import BuildIcon from '@mui/icons-material/Build';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import ConstructionIcon from '@mui/icons-material/Construction';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import AddPricesDialog from './AddPricesDialog';
import {
  mainProductConfig,
  quotationFormConfig,
} from '../../data/quotation/types';
import {
  getInitialTechnicalData,
  getSelectionTitle,
  getInitialGemaSystemData,
} from '../../utils/plantProposalUtils';
import ProductCard from './ProductCard';
import GemaSystemCard from './GemaSystemCard';
import SelectionOverlay from './SelectionOverlay';

const CONTENT_MAX_WIDTH = 800;

export default function NewQuotationView({
  onClose,
  onGenerateQuotation,
  initialQuotationData,
  selectedClient = null,
  onEditClient,
}) {
  const [selectedProduct, setSelectedProduct] = useState(mainProductConfig.options[0]);
  const [products, setProducts] = useState([]);
  const [expandedCardIds, setExpandedCardIds] = useState(() => new Set());
  const [selectionOverlayOpen, setSelectionOverlayOpen] = useState(false);
  const [selectionStack, setSelectionStack] = useState([]);

  const collapseAllCards = useCallback(() => {
    setExpandedCardIds(new Set());
  }, []);

  const overlayPages = useMemo(() => {
    const pages = [
      {
        key: 'root',
        label: 'Products',
        options: mainProductConfig.options,
      },
      ...selectionStack.map((item) => ({
        key: item.selected.value,
        label: item.selected.label,
        options: item.options,
      })),
    ];
    return pages;
  }, [selectionStack]);

  const getProductIcon = useCallback((productKey) => {
    switch (productKey) {
      case 'oven':
      case 'powder_coating_curing_oven':
      case 'batch_type_oven':
      case 'wood_sublimation_oven':
      case 'water_dry_off':
        return <KitchenIcon fontSize="large" sx={{ color: '#ff8c00' }} />;
      case 'conveyor':
      case 'inclusive_5T':
      case 'inclusive_7T':
      case 'four_wheel':
      case 'i_beam_x_348':
      case 'i_beam_x_458':
      case 'i_beam_x_678':
        return <LocalShippingIcon fontSize="large" sx={{ color: '#0288d1' }} />;
      case 'booth':
      case 'cartridge_filter_booth':
      case 'booth_with_reciprocator_cutout':
      case 'four_manual_cutouts':
      case 'two_manual_cutouts':
      case 'single_operator':
      case 'jumbo':
        return <HomeRepairServiceIcon fontSize="large" sx={{ color: '#43a047' }} />;
      case 'pt_line':
      case 'spray_pt_line':
      case 'dip_pt_line':
        return <OpacityIcon fontSize="large" sx={{ color: '#1976d2' }} />;
      case 'powder_transfer_system':
        return <SettingsApplicationsIcon fontSize="large" sx={{ color: '#6a1b9a' }} />;
      case 'dust_collector':
      case 'dc_3000':
      case 'dc_6000':
      case 'dc_8000':
      case 'dc_10000':
      case 'dc_12000':
      case 'dc_16000':
      case 'dc_20000':
      case 'dc_24000':
      case 'dc_28000':
      case 'dc_32000':
        return <BuildIcon fontSize="large" sx={{ color: '#f4511e' }} />;
      case 'reciprocator':
      case 'za_07':
      case 'za_10':
        return <PrecisionManufacturingIcon fontSize="large" sx={{ color: '#5d4037' }} />;
      case 'manual_gun':
      case 'gema_manual_gun':
        return <ConstructionIcon fontSize="large" sx={{ color: '#d32f2f' }} />;
      case 'gema_automatic_system':
        return <CategoryIcon fontSize="large" sx={{ color: '#455a64' }} />;
      default:
        return <BuildIcon fontSize="large" sx={{ color: '#616161' }} />;
    }
  }, []);

  useEffect(() => {
    if (initialQuotationData?.products?.length) {
      setProducts(initialQuotationData.products);
      setCommercialOfferData(initialQuotationData.commercialOffer || null);
      const key = initialQuotationData.selectedProductKey;
      if (key) {
        const opt = mainProductConfig.options.find((o) => o.value === key);
        if (opt) setSelectedProduct(opt);
      }
    }
  }, [initialQuotationData]);

  const openAddNewMenu = useCallback(() => {
    collapseAllCards();
    setSelectionStack([]);
    setSelectionOverlayOpen(true);
  }, [collapseAllCards]);

  const closeSelectionOverlay = useCallback(() => {
    setSelectionOverlayOpen(false);
    setSelectionStack([]);
  }, []);

  const handleOverlayBack = useCallback(() => {
    setSelectionStack((prev) => prev.slice(0, -1));
  }, []);

  const handleSelectionCardClick = useCallback(
    (option) => {
      if (selectionStack.length === 0 && option.value === 'gema_automatic_system') {
        const gemaData = getInitialGemaSystemData();
        setProducts((prev) => [
          ...prev,
          {
            id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
            productKey: 'gema_automatic_system',
            configKey: 'gemaAutomaticSystem',
            title: 'Gema Automatic System',
            components: gemaData.components,
          },
        ]);
        return;
      }

      if (selectionStack.length === 0 && option.configKey) {
        const nextOptions = quotationFormConfig[option.configKey]?.options ?? [];
        if (nextOptions.length > 0) {
          setSelectionStack([{ selected: option, options: nextOptions }]);
          return;
        }
      }

      if (option.options && option.options.length > 0) {
        setSelectionStack((prev) => [...prev, { selected: option, options: option.options }]);
        return;
      }

      const path = [
        ...selectionStack.map((s) => ({ value: s.selected.value, label: s.selected.label })),
        { value: option.value, label: option.label },
      ];
      const rootSelected = selectionStack[0]?.selected ?? option;
      const configKey = selectionStack[0]?.selected?.configKey ?? option.configKey;
      const productKey = selectionStack[0]?.selected?.value ?? option.value;
      const parentOption = selectionStack.length > 0 ? selectionStack[selectionStack.length - 1].selected : null;
      const title = getSelectionTitle(path);
      const initialTable = getInitialTechnicalData(productKey, path, option, parentOption);

      setSelectedProduct(rootSelected);
      setProducts((prev) => [
        ...prev,
        {
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          productKey,
          configKey,
          selectionPath: path,
          title,
          quantity: 1,
          technicalData: initialTable,
        },
      ]);
    },
    [selectionStack]
  );

  const updateProduct = useCallback((id, updates) => {
    setProducts((prev) => prev.map((p) => (p.id === id ? { ...p, ...updates } : p)));
  }, []);

  const removeProduct = useCallback((id) => {
    setProducts((prev) => prev.filter((p) => p.id !== id));
    setExpandedCardIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const setCardExpanded = useCallback((id, expanded) => {
    setExpandedCardIds((prev) => {
      const next = new Set(prev);
      if (expanded) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const [addPricesOpen, setAddPricesOpen] = useState(false);
  const [commercialOfferData, setCommercialOfferData] = useState(null);

  const handleGenerate = useCallback(() => {
    onGenerateQuotation({
      products,
      commercialOffer: commercialOfferData,
      selectedProductKey: selectedProduct?.value,
      selectedClient,
    });
  }, [products, commercialOfferData, selectedProduct, selectedClient, onGenerateQuotation]);

  const handleAddPricesSave = useCallback(({ products: updatedProducts, commercialOffer }) => {
    const keptIds = new Set(updatedProducts.map((p) => p.id));
    setProducts(updatedProducts);
    setCommercialOfferData(commercialOffer);
    setExpandedCardIds((prev) => {
      const next = new Set(prev);
      next.forEach((id) => {
        if (!keptIds.has(id)) next.delete(id);
      });
      return next;
    });
  }, []);

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1.5,
            px: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
            bgcolor: 'background.paper',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <IconButton size="small" onClick={onClose} aria-label="Close">
              <CloseIcon />
            </IconButton>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" fontWeight={600} sx={{ fontSize: '1.125rem' }}>
                New Quotation
              </Typography>
              {selectedClient?.companyName ? (
                <Typography variant="caption" color="text.secondary" display="block" noWrap title={selectedClient.label}>
                  Client: {selectedClient.companyName}
                </Typography>
              ) : null}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openAddNewMenu}
              sx={{ textTransform: 'none' }}
            >
              Add New
            </Button>
            {selectedClient && typeof onEditClient === 'function' ? (
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={onEditClient}
                sx={{ textTransform: 'none' }}
              >
                Edit Client
              </Button>
            ) : null}
            <Button
              variant="outlined"
              startIcon={<AttachMoneyIcon />}
              onClick={() => setAddPricesOpen(true)}
              disabled={products.length === 0}
              sx={{ textTransform: 'none' }}
            >
              Add Prices
            </Button>
            <Button
              variant="contained"
              onClick={handleGenerate}
              sx={{
                bgcolor: '#ffbe0b',
                color: '#000814',
                '&:hover': { bgcolor: '#e6ab0a' },
              }}
            >
              Generate Quotation
            </Button>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ flex: 1, overflow: 'auto', p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box sx={{ width: '100%', maxWidth: CONTENT_MAX_WIDTH }}>
                {products.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center', fontSize: 13 }}>
                    Click "Add New" to choose a product and add items. Choose the type from the selection overlay, then add more as needed.
                  </Typography>
                )}
                {products.map((p) =>
                  p.productKey === 'gema_automatic_system' && p.components ? (
                    <GemaSystemCard
                      key={p.id}
                      id={p.id}
                      title={p.title}
                      components={p.components}
                      expanded={expandedCardIds.has(p.id)}
                      onExpandedChange={(exp) => setCardExpanded(p.id, exp)}
                      onComponentsChange={(components) => updateProduct(p.id, { components })}
                      onDelete={() => removeProduct(p.id)}
                    />
                  ) : (
                    <ProductCard
                      key={p.id}
                      id={p.id}
                      title={p.title}
                      quantity={p.quantity}
                      technicalData={p.technicalData}
                      uploadedImages={p.uploadedImages}
                      expanded={expandedCardIds.has(p.id)}
                      onExpandedChange={(exp) => setCardExpanded(p.id, exp)}
                      onQuantityChange={(qty) => updateProduct(p.id, { quantity: qty })}
                      onTechnicalDataChange={(tech) => updateProduct(p.id, { technicalData: tech })}
                      onUploadedImagesChange={(images) => updateProduct(p.id, { uploadedImages: images })}
                      onDelete={() => removeProduct(p.id)}
                    />
                  )
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <SelectionOverlay 
        open={selectionOverlayOpen}
        pages={overlayPages}
        products={products}
        selectionStack={selectionStack}
        onClose={closeSelectionOverlay}
        onBack={handleOverlayBack}
        onBreadcrumbClick={(index) => setSelectionStack((prev) => prev.slice(0, index))}
        onOptionClick={handleSelectionCardClick}
        getProductIcon={getProductIcon}
      />
      <AddPricesDialog
        open={addPricesOpen}
        onClose={() => setAddPricesOpen(false)}
        products={products}
        commercialOffer={commercialOfferData}
        onSave={handleAddPricesSave}
      />
    </>
  );
}
